import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { useState, useEffect, useMemo, useRef } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Info, X, Plus, ChevronDown, ChevronRight, Trash2, ChevronLeft, Upload, FileText, Loader2 } from "lucide-react";
import { ArchitectureComponent } from "@/types/unified-model";
import { Switch } from "../ui/switch";
import { RadixSelect } from "../ui/select";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import {
  containsUnsafeHtml,
  containsUnsafeSql,
  HTTP_URL_ERROR,
  isSafeHttpUrl,
  NAME_UNSAFE_HTML_ERROR,
  NAME_UNSAFE_SQL_ERROR,
} from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";

const SQL_MAX_BYTES = 200 * 1024 * 1024;

type DataLakeUploadType = "Elastic" | "Relational" | "Non-Relational";

interface SampleFile {
  fileName: string;
  mimeType: string;
  content: string;
  label?: string;
}

interface FormatTemplate {
  extensions: string;
  description: string;
  samples: SampleFile[];
}

const FORMAT_TEMPLATES: Record<DataLakeUploadType, FormatTemplate> = {
  Elastic: {
    extensions: ".json",
    description:
      "Elasticsearch export format — each file's top-level key is the index name. Upload the mapping and settings files together (sharing the same index key) to populate fields plus shards/replicas.",
    samples: [
      {
        fileName: "products-mapping.json",
        label: "mapping sample",
        mimeType: "application/json",
        content: `{
  "products": {
    "mappings": {
      "properties": {
        "id":         { "type": "keyword" },
        "title":      { "type": "text" },
        "price":      { "type": "float" },
        "created_at": { "type": "date" }
      }
    }
  }
}`,
      },
      {
        fileName: "products-settings.json",
        label: "settings sample",
        mimeType: "application/json",
        content: `{
  "products": {
    "settings": {
      "index": {
        "number_of_shards": "1",
        "number_of_replicas": "1"
      }
    }
  }
}`,
      },
    ],
  },
  Relational: {
    extensions: ".sql",
    description:
      "DDL script containing CREATE TABLE / VIEW / INDEX / PROCEDURE statements. Data (INSERT) statements are ignored.",
    samples: [
      {
        fileName: "sample-schema.sql",
        mimeType: "application/sql",
        content: `CREATE TABLE users (
  id           INT PRIMARY KEY,
  email        VARCHAR(255) NOT NULL,
  display_name VARCHAR(120),
  created_at   TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

CREATE INDEX idx_users_email ON users(email);

CREATE TABLE orders (
  id       INT PRIMARY KEY,
  user_id  INT NOT NULL,
  total    DECIMAL(10,2) NOT NULL,
  CONSTRAINT fk_orders_user FOREIGN KEY (user_id) REFERENCES users(id)
);`,
      },
    ],
  },
  "Non-Relational": {
    extensions: ".json / .ndjson",
    description:
      'Either a schema ({ "collections": [...] }), a JSON array of documents, or NDJSON (one document per line). Data files are sampled to infer a schema.',
    samples: [
      {
        fileName: "sample-nosql-schema.json",
        mimeType: "application/json",
        content: `{
  "collections": [
    {
      "name": "users",
      "fields": [
        { "name": "_id",       "type": "ObjectId" },
        { "name": "email",     "type": "string"   },
        { "name": "createdAt", "type": "date"     }
      ]
    },
    {
      "name": "orders",
      "fields": [
        { "name": "_id",     "type": "ObjectId" },
        { "name": "userId",  "type": "ObjectId" },
        { "name": "total",   "type": "number"   },
        { "name": "items",   "type": "array"    }
      ]
    }
  ]
}`,
      },
    ],
  },
};

// Per-input character caps. Previously every input was maxLength: -1 (unbounded)
// which lets a paste bomb into name/description flow all the way through to the
// backend. Values below are generous but bounded — long enough for real content,
// short enough to make a stored-XSS payload obvious to a reviewer.
const NAME_MAX_LENGTH = 200;
const URL_MAX_LENGTH = 2048;
const STRING_MAX_LENGTH = 500;
const TEXTAREA_MAX_LENGTH = 5000;

// A field is a URL field when its key ends with `_url` — matches
// repository_url and access_url across all 8 architecture layers.
const isUrlFieldKey = (key: string) => /_url$/i.test(key);

interface FieldDef {
  key: string;
  label: string;
  type: "string" | "number" | "boolean" | "string[]" | "textarea" | "tables" | "select";
  required?: boolean;
  tooltip?: string;
  options?: string[];
}

interface Column {
  name: string;
  type: string;
  nullable?: boolean;
  primaryKey?: boolean;
  description?: string;
}

interface Table {
  name: string;
  description?: string;
  columns: Column[];
}

// ── Per-category field tooltip overrides ──────────────────────────────
// When a field has a category-specific meaning the override replaces the shared tooltip.
const CATEGORY_TOOLTIP_OVERRIDES: Record<string, Record<string, string>> = {
  event_queue: {
    technologies: "Broker implementations (Kafka for high-throughput streams, RabbitMQ for routing, SNS/SQS for cloud-native queuing)",
  },
};

// ── Shared field tooltips (used when no override exists) ─────────────
const SHARED_TOOLTIPS: Record<string, string> = {
  name: "Unique identifier/title for the component instance",
  category: "Broad classification grouping (e.g. frontend, networking, ai)",
  pattern: "Architectural or design patterns the component follows",
  technologies: "Specific tools, frameworks, or libraries used",
  protocols: "Communication standards used to send/receive data (HTTP, gRPC, etc.)",
  emits_events: "Whether this component publishes events to a message bus",
  metrics: "Telemetry signals this component exposes for monitoring",
  description: "Human-readable summary of what the component does",
  repository_url: "Link to the source code repository",
  code_ontology_id: "Reference ID linking to an internal code/service catalog",
  access_url: "URL where the component can be reached or its UI accessed",
  deployment: "How/where the component runs (e.g. Kubernetes)",
  // Entity-specific fields
  capabilities: "Features the gateway provides (routing, caching, transformation, load balancing)",
  auth_methods: "Supported authentication schemes (JWT, OAuth2, API Key, mTLS)",
  rate_limit: "Max allowed requests per time window to protect downstream services",
  alert_channels: "Where notifications are sent when thresholds are breached (email, Slack, PagerDuty)",
  pillers: "The three observability dimensions: metrics (numeric signals), logs (event records), traces (request journeys)",
  self_monitored: "Whether this system also monitors itself, not just other components",
  model_backend: "The underlying LLM powering the agent (GPT-4, Claude, LLaMA3, local model, etc.)",
  tools_available: "External capabilities the agent can invoke during reasoning (web search, code execution, file handling, database access)",
  domain: "The business capability this service owns (auth, billing, search, workflow, notifications)",
  model_type: "Category of ML model in use (classification, regression, clustering, recommendation, NLP)",
  vector_db: "Specialized database storing embedding vectors for semantic search and AI retrieval (Pinecone, Weaviate, Redis)",
  tables: "Define database tables and their column schemas for this data lake",
  type: "Data store type: Elastic (search/analytics), Relational (SQL/ACID), Non-Relational (NoSQL/document/key-value), or Graph (graph database schema)",
  cloud_provider: "Where compute runs: AWS, Azure, GCP, or on-premises hardware",
  regions: "Geographic deployment zones for latency, compliance, and redundancy",
  deployment_model: "How infrastructure is spread: multi-cloud (across providers), hybrid (cloud + on-prem), or fully on-prem",
  node_count: "Total number of compute nodes (VMs or physical machines) in the cluster",
  cpu_cores_total: "Aggregate CPU capacity across all nodes",
  storage_pb: "Total persistent storage capacity in petabytes",
  scalability: "How capacity changes under load: auto-scaling (dynamic) vs. manual (operator-driven)",
  backup_frequency: "How often data snapshots are taken for disaster recovery (daily, weekly, monthly)",
};

// Fields per entity category derived from the entity definitions
// Excludes system fields: architecturalId, projectUuid, id, createdAt, updatedAt
const ENTITY_FIELDS: Record<string, FieldDef[]> = {
  user_experience: [
    { key: "name", label: "Name", type: "string", required: true },
    { key: "description", label: "Description", type: "textarea" },
    { key: "category", label: "Category", type: "string" },
    { key: "protocols", label: "Protocols", type: "string[]" },
    { key: "technologies", label: "Technologies", type: "string[]" },
    { key: "pattern", label: "Pattern", type: "string[]" },
    { key: "emits_events", label: "Emits Events", type: "boolean" },
    { key: "metrics", label: "Metrics", type: "string[]" },
    { key: "repository_url", label: "Repository URL", type: "string" },
    { key: "code_ontology_id", label: "Code Ontology ID", type: "number" },
    { key: "access_url", label: "Access URL", type: "string" },
    { key: "deployment", label: "Deployment", type: "string" },
  ],
  api_gateway: [
    { key: "name", label: "Name", type: "string", required: true },
    { key: "description", label: "Description", type: "textarea" },
    { key: "category", label: "Category", type: "string" },
    { key: "pattern", label: "Pattern", type: "string[]" },
    { key: "protocols", label: "Protocols", type: "string[]" },
    { key: "technologies", label: "Technologies", type: "string[]" },
    { key: "capabilities", label: "Capabilities", type: "string[]" },
    { key: "auth_methods", label: "Auth Methods", type: "string[]" },
    { key: "rate_limit", label: "Rate Limit", type: "string" },
    { key: "emits_events", label: "Emits Events", type: "boolean" },
    { key: "metrics", label: "Metrics", type: "string[]" },
    { key: "repository_url", label: "Repository URL", type: "string" },
    { key: "code_ontology_id", label: "Code Ontology ID", type: "number" },
    { key: "access_url", label: "Access URL", type: "string" },
    { key: "deployment", label: "Deployment", type: "string" },
  ],
  observability_monitoring: [
    { key: "name", label: "Name", type: "string", required: true },
    { key: "description", label: "Description", type: "textarea" },
    { key: "category", label: "Category", type: "string" },
    { key: "pattern", label: "Pattern", type: "string[]" },
    { key: "technologies", label: "Technologies", type: "string[]" },
    { key: "alert_channels", label: "Alert Channels", type: "string[]" },
    { key: "pillers", label: "Pillars", type: "string[]" },
    { key: "emits_events", label: "Emits Events", type: "boolean" },
    { key: "metrics", label: "Metrics", type: "string[]" },
    { key: "self_monitored", label: "Self Monitored", type: "boolean" },
    { key: "access_url", label: "Access URL", type: "string" },
    { key: "deployment", label: "Deployment", type: "string" },
  ],
  agents: [
    { key: "name", label: "Name", type: "string", required: true },
    { key: "description", label: "Description", type: "textarea" },
    { key: "category", label: "Category", type: "string" },
    { key: "pattern", label: "Pattern", type: "string[]" },
    { key: "technologies", label: "Technologies", type: "string[]" },
    { key: "model_backend", label: "Model Backend", type: "string" },
    { key: "tools_available", label: "Tools Available", type: "string[]" },
    { key: "emits_events", label: "Emits Events", type: "boolean" },
    { key: "metrics", label: "Metrics", type: "string[]" },
    { key: "access_url", label: "Access URL", type: "string" },
    { key: "protocols", label: "Protocols", type: "string[]" },
    { key: "deployment", label: "Deployment", type: "string" },
  ],
  services: [
    { key: "name", label: "Name", type: "string", required: true },
    { key: "description", label: "Description", type: "textarea" },
    { key: "category", label: "Category", type: "string" },
    { key: "pattern", label: "Pattern", type: "string[]" },
    { key: "technologies", label: "Technologies", type: "string[]" },
    { key: "emits_events", label: "Emits Events", type: "boolean" },
    { key: "domain", label: "Domain", type: "string[]" },
    { key: "deployment", label: "Deployment", type: "string" },
    { key: "protocols", label: "Protocols", type: "string[]" },
    { key: "metrics", label: "Metrics", type: "string[]" },
    { key: "repository_url", label: "Repository URL", type: "string" },
    { key: "code_ontology_id", label: "Code Ontology ID", type: "number" },
    { key: "access_url", label: "Access URL", type: "string" },
  ],
  event_queue: [
    { key: "name", label: "Name", type: "string", required: true },
    { key: "description", label: "Description", type: "textarea" },
    { key: "category", label: "Category", type: "string" },
    { key: "pattern", label: "Pattern", type: "string[]" },
    { key: "technologies", label: "Technologies", type: "string[]" },
    { key: "protocols", label: "Protocols", type: "string[]" },
    { key: "emits_events", label: "Emits Events", type: "boolean" },
    { key: "metrics", label: "Metrics", type: "string[]" },
    { key: "access_url", label: "Access URL", type: "string" },
    { key: "deployment", label: "Deployment", type: "string" },
  ],
  data_lake: [
    { key: "name", label: "Name", type: "string", required: true },
    { key: "description", label: "Description", type: "textarea" },
    { key: "category", label: "Category", type: "string" },
    { key: "type", label: "Type", type: "select", options: ["Elastic", "Relational", "Non-Relational", "Graph"], required: true },
    { key: "pattern", label: "Pattern", type: "string[]" },
    { key: "technologies", label: "Technologies", type: "string[]" },
    { key: "model_type", label: "Model Type", type: "string" },
    { key: "vector_db", label: "Vector DB", type: "string" },
    { key: "emits_events", label: "Emits Events", type: "boolean" },
    { key: "metrics", label: "Metrics", type: "string[]" },
    { key: "access_url", label: "Access URL", type: "string" },
    { key: "deployment", label: "Deployment", type: "string" },
  ],
  infrastructure: [
    { key: "name", label: "Name", type: "string", required: true },
    { key: "description", label: "Description", type: "textarea" },
    { key: "category", label: "Category", type: "string" },
    { key: "pattern", label: "Pattern", type: "string[]" },
    { key: "technologies", label: "Technologies", type: "string[]" },
    { key: "cloud_provider", label: "Cloud Provider", type: "string" },
    { key: "regions", label: "Regions", type: "string[]" },
    { key: "deployment_model", label: "Deployment Model", type: "string" },
    { key: "node_count", label: "Node Count", type: "number" },
    { key: "cpu_cores_total", label: "CPU Cores Total", type: "number" },
    { key: "storage_pb", label: "Storage (PB)", type: "number" },
    { key: "scalability", label: "Scalability", type: "string" },
    { key: "backup_frequency", label: "Backup Frequency", type: "string" },
    { key: "emits_events", label: "Emits Events", type: "boolean" },
    { key: "metrics", label: "Metrics", type: "string[]" },
  ],
};

interface ArchitectureNodeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any, ingestFiles?: File[]) => void;
  node: ArchitectureComponent | null;
  category: string; // display name like "User Experience"
  projectUuid?: string;
  isSubmitting?: boolean;
}

// Convert display category back to snake_case key
function categoryToKey(category: string): string {
  return category.toLowerCase().replace(/ /g, "_").replace(/&_/g, "");
}

// Resolve the tooltip for a field: category override first, then shared fallback
function getFieldTooltip(catKey: string, fieldKey: string): string | undefined {
  return CATEGORY_TOOLTIP_OVERRIDES[catKey]?.[fieldKey] ?? SHARED_TOOLTIPS[fieldKey];
}

function FieldLabel({
  label,
  required,
  tooltip,
}: {
  label: string;
  required?: boolean;
  tooltip?: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="block text-sm font-medium text-text-primary">
        {label}
        {required && (
          <span className="text-error ml-1" aria-hidden="true">*</span>
        )}
      </span>
      {tooltip && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="text-muted hover:text-white transition-colors" tabIndex={-1} aria-label={`Help: ${label}`}>
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

function TablesColumnsInput({
  value,
  onChange,
}: {
  value: Table[];
  onChange: (val: Table[]) => void;
}) {
  const [expandedTables, setExpandedTables] = useState<Set<number>>(new Set());
  const [expandedColumns, setExpandedColumns] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Calculate pagination
  const totalPages = Math.ceil(value.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTables = value.slice(startIndex, endIndex);

  // Reset to page 1 if current page exceeds total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const toggleTable = (index: number) => {
    const newExpanded = new Set(expandedTables);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedTables(newExpanded);
  };

  const toggleColumn = (tableIndex: number, columnIndex: number) => {
    const key = `${tableIndex}-${columnIndex}`;
    const newExpanded = new Set(expandedColumns);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedColumns(newExpanded);
  };

  const addTable = () => {
    const newTableIndex = value.length;
    onChange([...value, { name: "", description: "", columns: [] }]);

    // Navigate to the page containing the new table
    const newTablePage = Math.ceil((newTableIndex + 1) / itemsPerPage);
    setCurrentPage(newTablePage);

    // Expand the new table
    setExpandedTables(new Set([...expandedTables, newTableIndex]));
  };

  const removeTable = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
    const newExpanded = new Set(expandedTables);
    newExpanded.delete(index);
    setExpandedTables(newExpanded);
  };

  const updateTable = (index: number, field: keyof Table, val: any) => {
    const updated = [...value];
    updated[index] = { ...updated[index], [field]: val };
    onChange(updated);
  };

  const addColumn = (tableIndex: number) => {
    const updated = [...value];
    updated[tableIndex].columns.push({
      name: "",
      type: "",
      nullable: false,
      primaryKey: false,
      description: "",
    });
    onChange(updated);
    const key = `${tableIndex}-${updated[tableIndex].columns.length - 1}`;
    setExpandedColumns(new Set([...expandedColumns, key]));
  };

  const removeColumn = (tableIndex: number, columnIndex: number) => {
    const updated = [...value];
    updated[tableIndex].columns = updated[tableIndex].columns.filter(
      (_, i) => i !== columnIndex
    );
    onChange(updated);
    const key = `${tableIndex}-${columnIndex}`;
    const newExpanded = new Set(expandedColumns);
    newExpanded.delete(key);
    setExpandedColumns(newExpanded);
  };

  const updateColumn = (
    tableIndex: number,
    columnIndex: number,
    field: keyof Column,
    val: any
  ) => {
    const updated = [...value];
    updated[tableIndex].columns[columnIndex] = {
      ...updated[tableIndex].columns[columnIndex],
      [field]: val,
    };
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      {paginatedTables.length > 0 && (
        <div className="space-y-3">
          {paginatedTables.map((table, paginatedIndex) => {
            const tableIndex = startIndex + paginatedIndex; // Actual index in value array
            return (
            <div
              key={tableIndex}
              className="border border-surface-border rounded-lg bg-surface-active/50 overflow-hidden"
            >
              {/* Table Header */}
              <div className="flex items-center justify-between p-3 bg-surface-active">
                <button
                  type="button"
                  onClick={() => toggleTable(tableIndex)}
                  className="flex items-center gap-2 flex-1 text-left"
                >
                  {expandedTables.has(tableIndex) ? (
                    <ChevronDown className="h-4 w-4 text-muted" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted" />
                  )}
                  <span className="text-sm font-medium text-white">
                    {table.name || `Table ${tableIndex + 1}`}
                  </span>
                  <span className="text-xs text-muted">
                    ({table.columns.length} columns)
                  </span>
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeTable(tableIndex)}
                  className="text-muted hover:text-error hover:bg-surface-hover h-8 w-8 p-0"
                  aria-label={`Remove table ${table.name || tableIndex + 1}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {/* Table Details */}
              {expandedTables.has(tableIndex) && (
                <div className="p-3 space-y-3">
                  {/* Table Name */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted">
                      Table Name <span className="text-error">*</span>
                    </label>
                    <Input
                      value={table.name}
                      onChange={(e) =>
                        updateTable(tableIndex, "name", e.target.value)
                      }
                      placeholder="e.g., users, transactions, products"
                      className="bg-surface text-white placeholder:text-muted text-sm h-9"
                    />
                  </div>

                  {/* Table Description */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted">
                      Description
                    </label>
                    <Textarea
                      value={table.description || ""}
                      onChange={(e) =>
                        updateTable(tableIndex, "description", e.target.value)
                      }
                      placeholder="Describe what this table stores..."
                      className="bg-surface text-white placeholder:text-muted text-sm min-h-[60px]"
                    />
                  </div>

                  {/* Columns Section */}
                  <div className="space-y-2 pt-2 border-t border-surface-border">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-white">
                        Columns
                      </label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addColumn(tableIndex)}
                        className="border-primary/50 text-primary hover:bg-primary/10 h-7 text-xs"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Column
                      </Button>
                    </div>

                    {table.columns.length > 0 ? (
                      <div className="space-y-1.5">
                        {table.columns.map((column, columnIndex) => {
                          const columnKey = `${tableIndex}-${columnIndex}`;
                          const isExpanded = expandedColumns.has(columnKey);

                          return (
                            <div
                              key={columnIndex}
                              className="border border-surface-border rounded bg-surface p-2"
                            >
                              {/* Column Header */}
                              <div className="flex items-center justify-between mb-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleColumn(tableIndex, columnIndex)
                                  }
                                  className="flex items-center gap-2 flex-1 text-left"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-3.5 w-3.5 text-muted" />
                                  ) : (
                                    <ChevronRight className="h-3.5 w-3.5 text-muted" />
                                  )}
                                  <span className="text-xs font-medium text-white">
                                    {column.name || `Column ${columnIndex + 1}`}
                                  </span>
                                  {column.type && (
                                    <span className="text-xs text-muted">
                                      : {column.type}
                                    </span>
                                  )}
                                  {column.primaryKey && (
                                    <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">
                                      PK
                                    </span>
                                  )}
                                </button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    removeColumn(tableIndex, columnIndex)
                                  }
                                  className="text-muted hover:text-error hover:bg-surface-hover h-6 w-6 p-0"
                                  aria-label={`Remove column ${column.name || columnIndex + 1}`}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>

                              {/* Column Details */}
                              {isExpanded && (
                                <div className="space-y-2 pl-5">
                                  {/* Column Name */}
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-muted">
                                      Name <span className="text-error">*</span>
                                    </label>
                                    <Input
                                      value={column.name}
                                      onChange={(e) =>
                                        updateColumn(
                                          tableIndex,
                                          columnIndex,
                                          "name",
                                          e.target.value
                                        )
                                      }
                                      placeholder="e.g., id, email, created_at"
                                      className="bg-surface-active text-white placeholder:text-muted text-xs h-8"
                                    />
                                  </div>

                                  {/* Column Type */}
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-muted">
                                      Type <span className="text-error">*</span>
                                    </label>
                                    <Input
                                      value={column.type}
                                      onChange={(e) =>
                                        updateColumn(
                                          tableIndex,
                                          columnIndex,
                                          "type",
                                          e.target.value
                                        )
                                      }
                                      placeholder="e.g., VARCHAR(255), INT, TIMESTAMP"
                                      className="bg-surface-active text-white placeholder:text-muted text-xs h-8"
                                    />
                                  </div>

                                  {/* Column Options */}
                                  <div className="flex gap-2">
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        checked={column.primaryKey || false}
                                        onCheckedChange={(checked) =>
                                          updateColumn(
                                            tableIndex,
                                            columnIndex,
                                            "primaryKey",
                                            checked
                                          )
                                        }
                                      />
                                      <span className="text-xs text-muted">
                                        Primary Key
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        checked={column.nullable || false}
                                        onCheckedChange={(checked) =>
                                          updateColumn(
                                            tableIndex,
                                            columnIndex,
                                            "nullable",
                                            checked
                                          )
                                        }
                                      />
                                      <span className="text-xs text-muted">
                                        Nullable
                                      </span>
                                    </div>
                                  </div>

                                  {/* Column Description */}
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-muted">
                                      Description
                                    </label>
                                    <Textarea
                                      value={column.description || ""}
                                      onChange={(e) =>
                                        updateColumn(
                                          tableIndex,
                                          columnIndex,
                                          "description",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Describe this column..."
                                      className="bg-surface-active text-white placeholder:text-muted text-xs min-h-[50px]"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-3 text-xs text-muted border border-dashed border-surface-border rounded">
                        No columns added yet
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls - only show when there are more than 5 tables */}
      {value.length > itemsPerPage && (
        <div className="flex items-center justify-between px-2 py-2 bg-surface-card rounded border border-surface-border">
          <span className="text-sm text-muted">
            Page {currentPage} of {totalPages} ({paginatedTables.length} tables this page) • Total: {value.length} tables
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Add Table Button */}
      <Button
        type="button"
        variant="outline"
        onClick={addTable}
        className="w-full border-dashed border-surface-border text-white hover:bg-surface-hover hover:border-primary/50"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Table
      </Button>
    </div>
  );
}

function ArrayFieldInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (val: string[]) => void;
  placeholder: string;
}) {
  const [input, setInput] = useState("");

  const handleAdd = () => {
    const trimmed = input.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInput("");
    }
  };

  return (
    <div className="space-y-1.5">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((item, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 bg-surface-active text-white text-xs px-2 py-1 rounded"
            >
              {item}
              <button
                type="button"
                onClick={() => onChange(value.filter((_, i) => i !== index))}
                className="text-muted hover:text-white"
                aria-label={`Remove ${item}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder={placeholder}
          className="bg-surface-active text-white placeholder:text-muted text-sm"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={!input.trim()}
          className="border-surface-border text-white hover:bg-surface-hover shrink-0"
        >
          Add
        </Button>
      </div>
    </div>
  );
}

function FormatGuidance({ type }: { type: DataLakeUploadType }) {
  const [expanded, setExpanded] = useState(false);
  const tmpl = FORMAT_TEMPLATES[type];
  const multi = tmpl.samples.length > 1;

  const handleDownload = (sample: SampleFile) => {
    const blob = new Blob([sample.content], { type: sample.mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = sample.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-md border border-surface-border bg-surface-active/30 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <div className="text-xs text-white space-y-1 min-w-0">
            <div>
              <span className="font-medium">Accepted:</span>{" "}
              <span className="text-muted">{tmpl.extensions} • max 200 MB each</span>
            </div>
            <div className="text-muted">{tmpl.description}</div>
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {tmpl.samples.map((sample) => (
            <Button
              key={sample.fileName}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleDownload(sample)}
              className="border-primary/50 text-primary hover:bg-primary/10 h-7 text-xs"
            >
              {multi ? `Download ${sample.label ?? sample.fileName}` : "Download sample"}
            </Button>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1 text-xs text-muted hover:text-white transition-colors"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {expanded ? (multi ? "Hide samples" : "Hide sample") : multi ? "View samples" : "View sample"}
      </button>
      {expanded && (
        <div className="space-y-2">
          {tmpl.samples.map((sample) => (
            <div key={sample.fileName} className="space-y-1">
              {multi && (
                <div className="text-xs text-muted font-medium">{sample.fileName}</div>
              )}
              <pre
                className={`text-xs bg-surface p-2 rounded border border-surface-border overflow-auto max-h-56 text-white whitespace-pre ${CUSTOM_SCROLLBAR}`}
              >
                {sample.content}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ArchitectureNodeDialog({
  isOpen,
  onClose,
  onSubmit,
  node,
  category,
  isSubmitting = false,
}: ArchitectureNodeDialogProps) {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const sqlFileInputRef = useRef<HTMLInputElement>(null);

  const catKey = useMemo(() => categoryToKey(category), [category]);
  const fields = useMemo(() => ENTITY_FIELDS[catKey] || ENTITY_FIELDS["services"] || [], [catKey]);
  const isDataLake = catKey === "data_lake";
  const isUpdate = !!node;
  const dataLakeType = formData.type as string | undefined;
  // Upload section is offered on create only — never during update.
  //   - Create + Elastic: multi-JSON upload (ES mapping/settings).
  //   - Create + Relational: single .sql upload.
  //   - Create + Non-Relational: multi-JSON upload (NoSQL schema files with a
  //     top-level `collections` array; the backend content-sniffs them apart
  //     from ES JSON and routes to /api/analyze-nosql).
  //   - Update (any type): no upload section.
  const isElasticUpload = isDataLake && !isUpdate && dataLakeType === "Elastic";
  const isRelationalCreateUpload = isDataLake && !isUpdate && dataLakeType === "Relational";
  const isNonRelationalUpload = isDataLake && !isUpdate && dataLakeType === "Non-Relational";
  const isJsonUpload = isElasticUpload || isNonRelationalUpload;
  const showUploadSection = isElasticUpload || isRelationalCreateUpload || isNonRelationalUpload;

  useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      if (sqlFileInputRef.current) sqlFileInputRef.current.value = "";
    }
  }, [isOpen]);

  // If the user flips the type to something that no longer supports upload,
  // drop any files they had queued so we don't accidentally ingest them.
  useEffect(() => {
    if (!showUploadSection && selectedFiles.length > 0) {
      setSelectedFiles([]);
      if (sqlFileInputRef.current) sqlFileInputRef.current.value = "";
    }
  }, [showUploadSection, selectedFiles.length]);

  useEffect(() => {
    if (isOpen) {
      const initial: Record<string, any> = {};
      const blueprint = node?.metadata?.blueprint;

      fields.forEach((field) => {
        if (node) {
          const val = blueprint?.[field.key] ?? (node as any)[field.key];
          if (field.type === "string[]") {
            initial[field.key] = Array.isArray(val) ? [...val] : [];
          } else if (field.type === "tables") {
            initial[field.key] = Array.isArray(val) ? [...val] : [];
          } else if (field.type === "boolean") {
            initial[field.key] = val === true;
          } else if (field.type === "number") {
            initial[field.key] = val ?? "";
          } else {
            initial[field.key] = val ?? "";
          }
        } else {
          if (field.type === "string[]") initial[field.key] = [];
          else if (field.type === "tables") initial[field.key] = [];
          else if (field.type === "boolean") initial[field.key] = false;
          else if (field.type === "number") initial[field.key] = "";
          else initial[field.key] = "";
        }
      });

      setFormData(initial);
    }
  }, [node, isOpen, fields]);

  const updateField = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    // Belt-and-braces: the submit button is disabled while any field has a
    // validation error, but re-check here so a stale render or keyboard-driven
    // submit can never sneak an XSS payload or javascript: URL past the client.
    const invalid = fields.some(
      (field) => getFieldError(field, formData[field.key]) !== null,
    );
    if (invalid) return;

    const data: Record<string, any> = {};
    fields.forEach((field) => {
      const val = formData[field.key];
      if (field.type === "number") {
        data[field.key] = val === "" ? undefined : Number(val);
      } else {
        data[field.key] = val;
      }
    });
    onSubmit(data, showUploadSection ? selectedFiles : []);
    setFormData({});
    setSelectedFiles([]);
    if (sqlFileInputRef.current) sqlFileInputRef.current.value = "";
  };

  const handleClose = () => {
    setFormData({});
    setSelectedFiles([]);
    if (sqlFileInputRef.current) sqlFileInputRef.current.value = "";
    onClose();
  };

  // Validate one picked file against the current data-lake type. Elastic
  // accepts any number of .json files; Relational create accepts a single
  // .sql file. Anything else is rejected with a toast.
  const handleFilesSelected = (incoming: FileList | null | undefined) => {
    if (!incoming || incoming.length === 0) return;
    const picks = Array.from(incoming);
    const allowJson = isJsonUpload;
    const allowSql = isRelationalCreateUpload;
    const jsonPattern = isNonRelationalUpload ? /\.(json|ndjson)$/i : /\.json$/i;
    const accepted: File[] = [];
    for (const file of picks) {
      const isJson = jsonPattern.test(file.name);
      const isSql = /\.sql$/i.test(file.name);
      if ((allowJson && !isJson) || (allowSql && !isSql)) {
        toast.error(
          allowJson
            ? `"${file.name}" is not a .json${isNonRelationalUpload ? " or .ndjson" : ""} file`
            : `"${file.name}" is not a .sql file`,
        );
        continue;
      }
      if (file.size > SQL_MAX_BYTES) {
        toast.error(`"${file.name}" exceeds 200 MB limit`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) return;
    setSelectedFiles((prev) => {
      // Relational create is single-file: replace any existing pick.
      if (allowSql) return [accepted[0]];
      // Elastic: append, de-duped by name+size.
      const key = (f: File) => `${f.name}::${f.size}`;
      const seen = new Set(prev.map(key));
      const merged = [...prev];
      for (const f of accepted) {
        if (!seen.has(key(f))) {
          merged.push(f);
          seen.add(key(f));
        }
      }
      return merged;
    });
  };

  const removeFileAt = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear when the cursor actually leaves the drop zone, not its children.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFilesSelected(e.dataTransfer?.files);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const isFieldEmpty = (field: FieldDef, val: any) => {
    if (val === undefined || val === null) return true;
    if (field.type === "string[]" || field.type === "tables") {
      return !Array.isArray(val) || val.length === 0;
    }
    if (field.type === "number") return val === "" || Number.isNaN(Number(val));
    if (field.type === "boolean") return false;
    return String(val).trim().length === 0;
  };

  // Return an inline validation error for a field, or null if the value is OK.
  // Applies only to string-typed inputs — number/boolean/select/array fields
  // have their own constraints handled by the widget.
  const getFieldError = (field: FieldDef, val: any): string | null => {
    if (field.type !== "string" && field.type !== "textarea") return null;
    const raw = typeof val === "string" ? val : val == null ? "" : String(val);
    if (raw.length === 0) return null;
    if (field.key === "name") {
      if (containsUnsafeHtml(raw)) return NAME_UNSAFE_HTML_ERROR;
      if (containsUnsafeSql(raw)) return NAME_UNSAFE_SQL_ERROR;
    }
    if (isUrlFieldKey(field.key) && !isSafeHttpUrl(raw)) {
      return HTTP_URL_ERROR;
    }
    return null;
  };

  const hasMissingRequired = fields.some(
    (field) => field.required && isFieldEmpty(field, formData[field.key])
  );

  const hasInvalidValues = fields.some(
    (field) => getFieldError(field, formData[field.key]) !== null
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {node ? `Edit ${category}` : `Create ${category}`}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {node ? `Edit the ${category} component fields` : `Create a new ${category} component`}
          </DialogDescription>
        </DialogHeader>

        <div className={`space-y-3 overflow-y-auto flex-1 px-1 ${CUSTOM_SCROLLBAR}`}>
          {fields.map((field) => {
            const val = formData[field.key];
            const tooltip = getFieldTooltip(catKey, field.key);

            if (field.type === "tables") {
              return (
                <div key={field.key} className="space-y-1.5">
                  <FieldLabel label={field.label} required={field.required} tooltip={tooltip} />
                  <TablesColumnsInput
                    value={val || []}
                    onChange={(v) => updateField(field.key, v)}
                  />
                </div>
              );
            }

            if (field.type === "string[]") {
              return (
                <div key={field.key} className="space-y-1.5">
                  <FieldLabel label={field.label} required={field.required} tooltip={tooltip} />
                  <ArrayFieldInput
                    value={val || []}
                    onChange={(v) => updateField(field.key, v)}
                    placeholder={`Add ${field.label.toLowerCase()}...`}
                  />
                </div>
              );
            }

            if (field.type === "boolean") {
              return (
                <div key={field.key} className="space-y-1.5">
                  <FieldLabel label={field.label} tooltip={tooltip} />
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={val === true}
                      onCheckedChange={(checked) => updateField(field.key, checked)}
                    />
                    <span className="text-sm text-muted">
                      {val ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              );
            }

            if (field.type === "textarea") {
              return (
                <div key={field.key} className="space-y-1.5">
                  <FieldLabel label={field.label} required={field.required} tooltip={tooltip} />
                  <Textarea
                    value={val ?? ""}
                    onChange={(e) => updateField(field.key, e.target.value)}
                    placeholder={`Enter ${field.label.toLowerCase()}...`}
                    className="bg-surface-active text-white placeholder:text-muted min-h-[80px] text-sm"
                    required={field.required}
                    aria-required={field.required || undefined}
                    maxLength={TEXTAREA_MAX_LENGTH}
                  />
                </div>
              );
            }

            if (field.type === "number") {
              return (
                <div key={field.key} className="space-y-1.5">
                  <FieldLabel label={field.label} required={field.required} tooltip={tooltip} />
                  <Input
                    type="number"
                    value={val ?? ""}
                    onChange={(e) => updateField(field.key, e.target.value)}
                    placeholder={`Enter ${field.label.toLowerCase()}...`}
                    className="bg-surface-active text-white placeholder:text-muted text-sm"
                    required={field.required}
                    aria-required={field.required || undefined}
                  />
                </div>
              );
            }

            if (field.type === "select") {
              const options = (field.options ?? []).map((opt) => ({ value: opt, label: opt }));
              // Data lake type is locked on update — switching type after
              // ingest would orphan the ingested ontology nodes.
              const lockedSelect =
                isDataLake && isUpdate && field.key === "type";
              return (
                <div key={field.key} className="space-y-1.5">
                  <FieldLabel label={field.label} required={field.required} tooltip={tooltip} />
                  <RadixSelect
                    options={options}
                    value={val || undefined}
                    onChange={(v) => updateField(field.key, v)}
                    placeholder={`Select ${field.label.toLowerCase()}...`}
                    disabled={lockedSelect}
                  />
                </div>
              );
            }

            // default: string
            const isNameField = field.key === "name";
            const isUrlField = isUrlFieldKey(field.key);
            const maxLength = isNameField
              ? NAME_MAX_LENGTH
              : isUrlField
                ? URL_MAX_LENGTH
                : STRING_MAX_LENGTH;
            const fieldError = getFieldError(field, val);
            return (
              <div key={field.key} className="space-y-1.5">
                <FieldLabel label={field.label} required={field.required} tooltip={tooltip} />
                <Input
                  type={isUrlField ? "url" : "text"}
                  value={val ?? ""}
                  onChange={(e) => updateField(field.key, e.target.value)}
                  placeholder={`Enter ${field.label.toLowerCase()}...`}
                  className="bg-surface-active text-white placeholder:text-muted text-sm"
                  required={field.required}
                  aria-required={field.required || undefined}
                  aria-invalid={fieldError ? true : undefined}
                  maxLength={maxLength}
                />
                {fieldError && (
                  <p className="text-error text-xs">{fieldError}</p>
                )}
              </div>
            );
          })}

          {showUploadSection && (
            <div className="space-y-2 pt-3 mt-3 border-t border-surface-border">
              <FieldLabel
                label={
                  isElasticUpload
                    ? "Upload Elasticsearch JSON (optional)"
                    : isNonRelationalUpload
                      ? "Upload NoSQL schema JSON (optional)"
                      : "Upload SQL (optional)"
                }
                tooltip={
                  isElasticUpload
                    ? "Attach one or more Elasticsearch export .json files keyed by index name (max 200 MB each). After save, each file is parsed in the background and the resulting indices/fields appear shortly."
                    : isNonRelationalUpload
                      ? "Attach one or more NoSQL .json files — a schema ({collections:[...]}), a JSON array of documents, or NDJSON (one document per line). Max 200 MB each. After save, each file is parsed (schemas) or sampled to infer a schema (data dumps) in the background, and the resulting collections/fields appear shortly."
                      : "Attach a .sql file (max 200 MB) — after the data lake is created, the file is parsed in the background and tables/columns/views/procedures appear shortly."
                }
              />
              {dataLakeType && FORMAT_TEMPLATES[dataLakeType as DataLakeUploadType] && (
                <FormatGuidance type={dataLakeType as DataLakeUploadType} />
              )}
              <div
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`rounded-lg border border-dashed p-3 space-y-2 transition-colors ${
                  isDragging
                    ? "border-primary bg-primary/10"
                    : "border-surface-border bg-surface-active/40"
                }`}
              >
                <input
                  ref={sqlFileInputRef}
                  type="file"
                  accept={
                    isNonRelationalUpload
                      ? ".json,.ndjson,application/json,application/x-ndjson,text/json"
                      : isJsonUpload
                        ? ".json,application/json,text/json"
                        : ".sql,text/x-sql,application/sql"
                  }
                  multiple={isJsonUpload}
                  className="hidden"
                  onChange={(e) => {
                    handleFilesSelected(e.target.files);
                    // Allow re-selecting the same file after removal.
                    if (sqlFileInputRef.current) sqlFileInputRef.current.value = "";
                  }}
                />

                {selectedFiles.length > 0 && (
                  <div className="space-y-1.5">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${file.size}-${index}`}
                        className="flex items-center justify-between gap-3 rounded bg-surface-card/40 px-2 py-1.5"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="h-4 w-4 text-primary shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm text-white truncate">{file.name}</div>
                            <div className="text-xs text-muted">{formatFileSize(file.size)}</div>
                          </div>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeFileAt(index)}
                          className="text-muted hover:text-error h-8 w-8 p-0 shrink-0"
                          aria-label={`Remove ${file.name}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {(isJsonUpload || selectedFiles.length === 0) && (
                  <button
                    type="button"
                    onClick={() => sqlFileInputRef.current?.click()}
                    className="w-full flex flex-col items-center justify-center gap-1 py-3 text-sm text-muted hover:text-white transition-colors"
                  >
                    <Upload className="h-5 w-5" />
                    <span>
                      {isJsonUpload
                        ? selectedFiles.length === 0
                          ? isNonRelationalUpload
                            ? "Click to select or drag & drop one or more .json / .ndjson files"
                            : "Click to select or drag & drop one or more .json files"
                          : isNonRelationalUpload
                            ? "Add more .json / .ndjson files"
                            : "Add more .json files"
                        : "Click to select or drag & drop a .sql file"}
                    </span>
                    <span className="text-xs">Max 200 MB each</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-3 pt-2 border-t border-surface-border">
          <Button
            onClick={handleSubmit}
            disabled={hasMissingRequired || hasInvalidValues || isSubmitting}
            className="bg-primary hover:bg-primary-dark text-button-text"
          >
            {isSubmitting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {node ? "Update" : "Create"}
          </Button>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="bg-surface border border-surface-border hover:bg-surface-hover hover:border-surface-border text-white"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}